import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { ScrollPane } from '@components'
import PropTypes from 'prop-types'

const useStyles = makeStyles(() => ({
  container: {
    margin: '16px 0',
  },
  errorDescription: {
    margin: '6px 0',
  },
  errorTitle: {
    margin: '6px 0',
    fontWeight: 'bold',
  },
}))

export const BulkUploadError = props => {
  const classes = useStyles()

  const errorCodes = Object.freeze({
    MISSING_HEADERS: 4100,
    EXTRA_HEADERS: 4200,
    NO_HEADERS: 4202,
    DUPLICATED_HEADERS: 4203,
    EMPTY_FILE: 4204,
    UNSUPPORTED_ENCODING: 4300,
    CELLS_WITH_INVALID_LENGTH: 4301,
  })

  function getErrorComponentByError(error) {
    let errorCount = 0
    switch (error.errorCode) {
      case errorCodes.MISSING_HEADERS:
        errorCount = error.errorData.missingHeaders.length
        return (
          <div>
            <Typography variant="body1">
              <Trans>The provided file is missing the following {errorCount} fields:</Trans>
            </Typography>
            <ScrollPane>
              {error.errorData.missingHeaders.map((missingHeader, i) => (
                <div key={i}>{missingHeader}</div>
              ))}
            </ScrollPane>
          </div>
        )
      case errorCodes.EXTRA_HEADERS:
        errorCount = error.errorData.extraHeaders.length
        return (
          <>
            <Typography variant="body1">
              <Trans>
                The provided file includes {errorCount} extra fields that must be removed:
              </Trans>
            </Typography>
            <ScrollPane>
              {error.errorData.extraHeaders.map((missingHeader, i) => (
                <div key={i}>{missingHeader}</div>
              ))}
            </ScrollPane>
          </>
        )
      case errorCodes.DUPLICATED_HEADERS:
        errorCount = error.errorData.duplicatedHeaders.length
        return (
          <>
            <Typography variant="body1">
              <Trans>
                The provided file has {errorCount} duplicated fields that must be removed:
              </Trans>
            </Typography>
            <ScrollPane>
              {error.errorData.duplicatedHeaders.map((duplicatedHeader, i) => (
                <div key={i}>{duplicatedHeader}</div>
              ))}
            </ScrollPane>
          </>
        )
      case errorCodes.UNSUPPORTED_ENCODING:
        return (
          <div>
            <Typography variant="body1" className={classes.errorDescription}>
              {error.defaultErrorMessage}
            </Typography>
            <Typography variant="body1" className={classes.errorDescription}>
              {t`The following encodings are supported:`}
            </Typography>
            <ScrollPane>
              {error.errorData.supportedEncodings.map((supportedEncoding, i) => (
                <div key={i}>{supportedEncoding}</div>
              ))}
            </ScrollPane>
            <Typography className="error-title">
              {t`Please re-save your CSV file as UTF-8. Reach out to your administrator for further assistance.`}
            </Typography>
          </div>
        )
      case errorCodes.CELLS_WITH_INVALID_LENGTH:
        errorCount = error.errorData.cellsWithInvalidLength.length
        return (
          <div>
            <Typography variant="body1">
              <Trans>
                The provided file includes {errorCount} cell(s) that exceed(s) the maximum of 4096
                characters:
              </Trans>
            </Typography>
            <ScrollPane>
              {error.errorData.cellsWithInvalidLength.map((cellWithInvalidLength, i) => {
                const invalidRowLength = cellWithInvalidLength.row
                const invalidColumnLength = cellWithInvalidLength.column
                return (
                  <div key={i}>
                    <Trans>
                      Row: {invalidRowLength}, column: {invalidColumnLength}
                    </Trans>
                  </div>
                )
              })}
            </ScrollPane>
          </div>
        )
      case errorCodes.NO_HEADERS:
        return (
          <div>
            <Typography variant="body1">
              {t`The provided file doesn't include any headers. Please include headers in the first row of the file.`}
            </Typography>
          </div>
        )
      case errorCodes.EMPTY_FILE:
        return (
          <div>
            <Typography variant="body1">
              {t`The specified file must contain lines beyond the header.`}
            </Typography>
          </div>
        )
      default:
        return (
          <div>
            <Typography variant="body1">{t`The provided file cannot be processed`}</Typography>
            <Typography variant="body1" className={classes.errorDescription}>
              {t`Please review the file format, headers, and data and try again.`}
            </Typography>
          </div>
        )
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.15 } }}
      exit={{ opacity: 0, transition: { duration: 0.15 } }}
      className={classes.container}
    >
      {getErrorComponentByError(props.error)}
    </motion.div>
  )
}

BulkUploadError.propTypes = {
  error: PropTypes.object.isRequired,
}
